import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useMemo, useState } from "react";
import ListItemCard from "../../ListItemCard";
import MobileSheet from "@widget/popups/MobileSheet";
import ActionsListComponent from "./ActionListComponent";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import ButtonListContext from "@widget/buttons/ButtonListContext";
import { UXPContext } from "@core/context/Context";
import MobileToolbar from "@widget/other/MobileToolbar";
// A phone card shows the title and this many lines (3 columns); a tap on the row shows the rest
const CARD_LINES = 2;
const contentColumns = (columns) => columns.filter(c => !c.id.startsWith('__'));
const cellValue = (c, item, index) => c.renderColumn ? c.renderColumn(item, index) : item[c.id];
/**
 * Phones: one column that shows a row as a card. The first content column is the title; the
 * next two are label / value lines under it. Columns whose id starts with `__` are left out.
 * Used when an app gives no phone column of its own (tables, object search lists).
 */
export const buildCardColumn = (columns) => {
    const [first, ...rest] = contentColumns(columns);
    return {
        id: '__card__',
        label: '',
        renderColumn: (item, index) => _jsx(ListItemCard, { title: first ? cellValue(first, item, index) : null, lines: rest.slice(0, CARD_LINES).map(c => ({ label: c.label, value: cellValue(c, item, index) })) }),
    };
};
/** The row actions a table's action columns give (ObjectSearch defaultActionColumns, or a table's own columns) */
export const rowActionsOf = (d) => {
    var _a, _b, _c;
    return ({
        edit: ((_a = d === null || d === void 0 ? void 0 : d.editColumn) === null || _a === void 0 ? void 0 : _a.enable) && d.editColumn.onEdit ? { label: d.editColumn.label, onClick: d.editColumn.onEdit, visible: d.editColumn.visible } : undefined,
        rowActions: ((_b = d === null || d === void 0 ? void 0 : d.actionsColumn) === null || _b === void 0 ? void 0 : _b.enable) ? d.actionsColumn.rowActions : undefined,
        delete: ((_c = d === null || d === void 0 ? void 0 : d.deleteColumn) === null || _c === void 0 ? void 0 : _c.enable) && d.deleteColumn.onDelete ? { label: d.deleteColumn.label, onClick: d.deleteColumn.onDelete, visible: d.deleteColumn.visible } : undefined,
    });
};
export const hasRowActions = (a) => !!(a.edit || a.rowActions || a.delete);
const shows = (row, a) => !!a && (!a.visible || a.visible(row));
/**
 * A row's actions on phones, for a button that stands for them: `empty` when the row has none,
 * `one` when a single action with an icon can show as its own button (otherwise use ⋯).
 */
export const summariseRowActions = (row, a) => {
    const resolved = a.rowActions ? (typeof a.rowActions === 'function' ? a.rowActions(row) : a.rowActions) : [];
    // A rowActions function may render its own content: then there is always something to show
    const custom = Array.isArray(resolved) ? resolved.filter(x => shows(row, x)) : null;
    const list = [
        ...(shows(row, a.edit) ? [{ icon: 'phl pencil-simple', label: a.edit.label || 'uxp-core.text.edit', onClick: a.edit.onClick, danger: false }] : []),
        ...(custom || []).map(x => ({ icon: x.icon, label: x.label, onClick: 'onClick' in x ? x.onClick : undefined, danger: false })),
        ...(shows(row, a.delete) ? [{ icon: 'phl trash', label: a.delete.label || 'uxp-core.text.delete', onClick: a.delete.onClick, danger: true }] : []),
    ];
    const one = custom && list.length === 1 && list[0].icon && list[0].onClick ? list[0] : null;
    return { empty: !list.length && !!custom, one };
};
/**
 * Phones: a row's actions as labelled rows in a sheet. Use inside a sheet list
 * (`ActionsSheet`, or `ButtonListContext` with `onAction` closing the sheet).
 * The sheet closes before an action runs, so a confirm or form it opens isn't shown over it.
 */
export const RowActionButtons = ({ row, actions, onClose }) => {
    const context = useContext(UXPContext);
    const closer = useMemo(() => ({ current: { close: onClose } }), [onClose]);
    const custom = useMemo(() => {
        const resolved = typeof actions.rowActions === 'function' ? actions.rowActions(row) : actions.rowActions;
        if (!Array.isArray(resolved))
            return resolved;
        return resolved.map(a => 'onClick' in a ? Object.assign(Object.assign({}, a), { onClick: (item) => { onClose(); return a.onClick(item); } }) : a);
    }, [actions.rowActions, row, onClose]);
    return _jsxs(_Fragment, { children: [shows(row, actions.edit) && _jsx(ButtonComponent, { title: context.$L(actions.edit.label || 'uxp-core.text.edit'), leftIcon: "phl pencil-simple", variant: "secondary", onClick: () => actions.edit.onClick(row) }), actions.rowActions && _jsx(ActionsListComponent, { actions: custom, item: row, parentDropdownRef: closer }), shows(row, actions.delete) && _jsx(ButtonComponent, { title: context.$L(actions.delete.label || 'uxp-core.text.delete'), leftIcon: "phl trash", variant: "danger", onClick: () => actions.delete.onClick(row) })] });
};
/**
 * Phones: every field of a row, in a sheet. A tap on a row opens it when the row has no tap
 * action of its own, so the columns its card leaves out can still be read. The row's
 * actions follow the fields.
 */
export const RowDetailsSheet = ({ columns, row, onClose, actions }) => {
    const context = useContext(UXPContext);
    const [first, ...rest] = contentColumns(columns);
    const title = row && first ? cellValue(first, row, 0) : undefined;
    const [moreOpen, setMoreOpen] = useState(false);
    // An action closes both sheets first, so a confirm or form it opens isn't shown over them
    const closeAll = () => { setMoreOpen(false); onClose(); };
    const listMode = useMemo(() => ({ onAction: closeAll }), [onClose]);
    const summary = row && actions && hasRowActions(actions) ? summariseRowActions(row, actions) : null;
    const one = summary === null || summary === void 0 ? void 0 : summary.one;
    return _jsxs(MobileSheet, { isOpen: !!row, onClose: onClose, title: title, showClose: true, children: [row && _jsx("div", { className: "uxpcore_rowdetails", children: _jsx(ListItemCard, { title: null, lines: rest.map(c => ({ label: c.label, value: cellValue(c, row, 0) })) }) }), row && summary && !summary.empty && _jsx(MobileToolbar, { className: "uxpcore_rowdetails__toolbar", actions: one
                    ? [{ id: 'action', icon: one.icon, label: typeof one.label === 'string' ? context.$L(one.label) : '', onClick: () => { onClose(); one.onClick(row); } }]
                    : [{ id: 'more', icon: 'phr dots-three', label: context.$L('uxp-core.text.actions'), active: moreOpen, onClick: () => setMoreOpen(true) }] }), row && actions && _jsx(MobileSheet, { isOpen: moreOpen, onClose: () => setMoreOpen(false), title: title, children: _jsx(ButtonListContext.Provider, { value: listMode, children: _jsx("div", { className: "uxpcore_sheetlist", children: _jsx(RowActionButtons, { row: row, actions: actions, onClose: closeAll }) }) }) })] });
};
